import type {
  PreconditionBundle,
  PreconditionItem,
  PreconditionPushResult,
  PreconditionStageEntry,
  PreconditionVerifyStatus,
} from '@sr-test-wizard/shared';
import { api } from './api';

/**
 * Phase 2 (Preconditions) API client. Mirrors the
 * bug-review / env-links pattern: GET returns whatever is on disk (cheap),
 * POST /pull refreshes from Jira, PATCH /items/:id stages a local edit,
 * POST /items/:id/verify runs an auto-checker, POST /push commits all
 * staged edits back to the Jira Smart Checklist as a single approved batch.
 */
export const preconditionsApi = {
  get: (runId: string) =>
    api.get<PreconditionBundle | null>(`/api/runs/${runId}/preconditions`),
  pull: (runId: string) =>
    api.post<PreconditionBundle>(`/api/runs/${runId}/preconditions/pull`),
  stage: (runId: string, itemId: string, patch: PreconditionStageEntry) =>
    api.patch<PreconditionBundle>(
      `/api/runs/${runId}/preconditions/items/${encodeURIComponent(itemId)}`,
      patch,
    ),
  verify: (runId: string, itemId: string) =>
    api.post<{
      item: PreconditionItem;
      suggestion: { status: PreconditionVerifyStatus; evidence: string };
    }>(`/api/runs/${runId}/preconditions/items/${encodeURIComponent(itemId)}/verify`),
  verifyAll: (runId: string) =>
    api.post<{
      bundle: PreconditionBundle;
      summary: {
        total: number;
        byStatus: Record<PreconditionVerifyStatus, number>;
        failed: number;
        elapsedMs: number;
      };
    }>(`/api/runs/${runId}/preconditions/verify-all`),
  push: (runId: string) =>
    api.post<{ bundle: PreconditionBundle; result: PreconditionPushResult }>(
      `/api/runs/${runId}/preconditions/push`,
    ),
};
